import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import axios from 'axios';
import { apiService } from '../services/apiService';
import { websocketService } from '../services/websocketService';
import {
  getAccessToken,
  setTokens,
  clearTokens,
  isPerTab as isPerTabSession,
} from '../services/authStorage';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Track the token value this tab has already consumed so we can detect
  // cross-tab changes. The storage event fires in *other* tabs when a tab
  // mutates localStorage, so the Control Center picks up a login/logout
  // performed on the Operations view and vice versa.
  const lastKnownTokenRef = useRef(null);

  useEffect(() => {
    // If this window was launched as a child popup (e.g. admin control
    // center opened from the operator view via window.open with
    // ?logout_close=1), persist the marker so we self-close on logout
    // — both for our own logout and for a logout in the parent tab
    // detected via the storage event below. SessionStorage scope is
    // exactly right: survives in-window navigation, dies with the tab.
    try {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get('logout_close') === '1') {
        sessionStorage.setItem('logout_close_on_remote', '1');
      }
    } catch (_) { /* */ }

    // Check if user is already logged in (stored token). Per-tab scope
    // (sessionStorage) wins over shared scope (localStorage) so
    // multi-account windows can carry a different identity than the
    // one shared across tabs.
    const token = getAccessToken();
    lastKnownTokenRef.current = token;
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUserInfo();
    } else {
      setLoading(false);
    }

    // Cross-tab auth sync only applies when this tab is operating in the
    // shared (localStorage) scope. A tab whose own session is per-tab
    // must ignore localStorage changes emitted by other tabs — the
    // whole point of per-tab mode is that those other tabs never touch
    // this window's identity. We re-check the scope inside the handler
    // (not just at mount) so a tab that becomes per-tab mid-session via
    // login() also stops reacting.
    const onStorage = (e) => {
      if (!e || e.key !== 'auth_token') return;
      if (isPerTabSession()) return;
      const newToken = e.newValue;
      if (newToken === lastKnownTokenRef.current) return;
      lastKnownTokenRef.current = newToken;

      if (!newToken) {
        console.log('[AUTH] Another tab logged out — clearing session here');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
        // If we're a child popup (admin control center launched from
        // the operator view), close ourselves so the user is left
        // with just one /login window instead of two.
        if (sessionStorage.getItem('logout_close_on_remote') === '1') {
          try { window.close(); } catch (_) { /* */ }
        }
        return;
      }

      console.log('[AUTH] Another tab changed the auth token — refetching user');
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setLoading(true);
      fetchUserInfo();
    };
    window.addEventListener('storage', onStorage);

    // Observatory lifecycle broadcasts that may invalidate this user's
    // effective access (permanent delete or deregister). Re-run
    // /v1/auth/me so the in-memory user.permissions list drops the
    // observatory. Per-view consumers that care about "this specific
    // observatory just vanished" can subscribe to the same event
    // directly via useWebSocketEvents and navigate away as needed.
    const onObservatoryDeleted = () => {
      if (!lastKnownTokenRef.current) return; // not logged in here
      console.log('[AUTH] observatory_deleted received — refreshing credentials');
      fetchUserInfo();
    };
    websocketService.on('observatory_deleted', onObservatoryDeleted);

    return () => {
      window.removeEventListener('storage', onStorage);
      websocketService.off('observatory_deleted', onObservatoryDeleted);
    };
  }, []);

  const fetchUserInfo = async () => {
    try {
      // Validate token with server using apiService (which has refresh interceptor)
      const response = await apiService.get('/v1/auth/me');
      
      if (response.data.success) {
        const userData = response.data.data;
        
        // Set user with ALL server response data
        setUser({
          id: userData.id,
          email: userData.email,
          name: userData.name,
          first_name: userData.first_name,
          last_name: userData.last_name,
          username: userData.username,
          role: userData.role,
          institution: userData.institution,
          institution_id: userData.institution_id,
          institution_name: userData.institution?.name,
          permissions: userData.permissions || [],
          email_verified: userData.email_verified,
          last_login_at: userData.last_login_at,
          created_at: userData.created_at,
          preferences: userData.preferences || {},
          avatar_url: userData.avatar_url || null
        });

        console.log('[AUTH CHECK] User authenticated:', userData.email, 'Role:', userData.role);
      } else {
        throw new Error('Token validation failed');
      }
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      // If token is invalid, clear both scopes
      clearTokens();
      lastKnownTokenRef.current = null;
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, opts = {}) => {
    const perTab = !!opts.perTab;
    try {
      setError(null);

      // Use the real login endpoint via apiService
      const response = await apiService.post('/v1/auth/login', {
        email,
        password
      });

      if (response.data.success) {
        const { token, accessToken, refreshToken, user: userData } = response.data.data;

        // Store the JWT tokens (use accessToken if available, fall back to token for backward compat)
        const authToken = accessToken || token;
        setTokens({ accessToken: authToken, refreshToken }, { perTab });
        lastKnownTokenRef.current = authToken;
        axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;

        // Set user with ALL server response data
        const user = {
          id: userData.id,
          email: userData.email,
          name: userData.name,
          first_name: userData.first_name,
          last_name: userData.last_name,
          username: userData.username,
          role: userData.role,
          institution: userData.institution,
          institution_id: userData.institution_id,
          institution_name: userData.institution?.name,
          permissions: userData.permissions || [],
          email_verified: userData.email_verified,
          last_login_at: userData.last_login_at,
          is_first_login: userData.is_first_login || false,
          created_at: userData.created_at,
          preferences: userData.preferences || {},
          avatar_url: userData.avatar_url || null
        };

        setUser(user);
        console.log('[AUTH] User logged in:', user.email, 'Role:', user.role);

        return { success: true };
      } else {
        throw new Error(response.data.error || 'Login failed');
      }

    } catch (error) {
      let message = 'Login failed';
      if (error.response?.data?.error) {
        // Handle API error object format {code, message}
        const apiError = error.response.data.error;
        message = typeof apiError === 'object' ? apiError.message : apiError;
      } else if (error.message) {
        message = error.message;
      }
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint (optional - mainly for audit logging)
      await apiService.post('/v1/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local state regardless
      clearTokens();
      lastKnownTokenRef.current = null;
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      // Child popups self-close on logout so the parent tab is the
      // single surviving /login window. The cross-tab storage event
      // handles the inverse direction (logout from parent → child
      // closes itself when it sees the cleared token).
      if (sessionStorage.getItem('logout_close_on_remote') === '1') {
        try { window.close(); } catch (_) { /* */ }
      }
    }
  };

  const updateUserPreferences = async (prefs) => {
    // Update local state immediately for cross-page consistency
    setUser(prev => prev ? {
      ...prev,
      preferences: { ...prev.preferences, ...prefs }
    } : prev);
    // Save to server and return the promise so callers can await.
    // (apiService.updatePreferences no-ops the server write during a read-only
    // impersonation session — see there.)
    return apiService.updatePreferences(prefs).catch(err => {
      console.error('Failed to save user preferences:', err);
      throw err;
    });
  };

  const register = async (userData) => {
    try {
      setError(null);
      // TODO: Replace with actual API endpoint when auth is implemented
      const response = await apiService.post('/v1/auth/register', userData);
      const { token, user } = response.data.data;
      
      // Store token (new sign-ups stay in the shared scope by default;
      // users can opt into per-tab on the login screen afterwards).
      setTokens({ accessToken: token });
      lastKnownTokenRef.current = token;
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Set user
      setUser(user);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Registration failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const value = {
    user,
    setUser,
    loading,
    error,
    login,
    logout,
    register,
    updateUserPreferences,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};